import { useState } from 'react';

import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { GroupLabel } from '@/cam-admin/control-panel/bits';
import SearchField from '@/components/mui/SearchField';
import {
  type ActionItem,
  firstName,
  initials,
} from '@/cam-admin/control-panel/fixtures';

const stop = (e: React.MouseEvent) => e.stopPropagation();

// 'Every day · 14:00' for recurring, 'By Thu 28 May, 14:00' for dated.
const scheduleLine = (a: ActionItem) => {
  if (a.repeats) return a.time ? `${a.repeats} · ${a.time}` : a.repeats;
  if (a.due) return a.time ? `By ${a.due}, ${a.time}` : `By ${a.due}`;
  return null;
};

const ActionRow = ({
  action,
  onToggleDone,
  onOpen,
}: {
  action: ActionItem;
  onToggleDone: () => void;
  onOpen: () => void;
}) => (
  <Card variant='listItem' sx={{ px: 2, py: 1 }} onClick={onOpen}>
    <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
      <Box sx={{ display: 'flex' }} onClick={stop}>
        <Checkbox
          slotProps={{
            input: { 'aria-label': `Mark ${action.title} as done` },
          }}
          checked={Boolean(action.done)}
          onChange={onToggleDone}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant='body1'>{action.title}</Typography>
        {scheduleLine(action) && (
          <Typography
            variant='body2'
            sx={{
              color:
                action.overdue && !action.done
                  ? 'error.main'
                  : 'text.secondary',
            }}
          >
            {scheduleLine(action)}
          </Typography>
        )}
      </Box>
      {action.assignee ? (
        <>
          <Typography variant='body2' color='text.secondary'>
            {firstName(action.assignee)}
          </Typography>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'custom.neutral.300',
              color: 'common.white',
              fontSize: 13,
            }}
          >
            {initials(action.assignee)}
          </Avatar>
        </>
      ) : (
        <Typography variant='body2' color='text.disabled'>
          Unassigned
        </Typography>
      )}
    </Stack>
  </Card>
);

// Actions: a week-at-a-glance list with a search bar. Viewing, creating and
// editing all happen in the ActionDrawer (rendered by ControlPanel over the
// frame), opened via onView / onCreate.
const ActionsTab = ({
  actions,
  onCreate,
  onView,
  onToggleDone,
}: {
  actions: ActionItem[];
  onCreate: () => void;
  onView: (id: string) => void;
  onToggleDone: (id: string) => void;
}) => {
  const [query, setQuery] = useState('');
  // Done history grows without bound, so it stays collapsed at the bottom of
  // the list until the CAM asks for it (a search opens it).
  const [doneOpen, setDoneOpen] = useState(false);

  const q = query.trim().toLowerCase();
  const matches = (a: ActionItem) =>
    !q ||
    a.title.toLowerCase().includes(q) ||
    Boolean(a.assignee?.toLowerCase().includes(q));
  const found = actions.filter(matches);
  const openActions = found.filter((a) => !a.done);
  const doneActions = found.filter((a) => a.done);

  // Open work, grouped by urgency. Done work collects in the collapsed section
  // at the bottom.
  const openGroups: {
    label: string;
    error?: boolean;
    items: ActionItem[];
  }[] = [
    {
      label: 'Overdue',
      error: true,
      items: openActions.filter((a) => a.overdue),
    },
    {
      label: 'This week',
      items: openActions.filter((a) => (a.due || a.repeats) && !a.overdue),
    },
    {
      label: 'No date',
      items: openActions.filter((a) => !a.due && !a.repeats),
    },
  ];

  // A search opens the done section so matches there aren't hidden.
  const showDone = doneOpen || q.length > 0;
  const nothing = found.length === 0;

  const rowFor = (a: ActionItem) => (
    <ActionRow
      key={a.id}
      action={a}
      onToggleDone={() => onToggleDone(a.id)}
      onOpen={() => onView(a.id)}
    />
  );

  return (
    <Stack spacing={2}>
      <Stack
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        direction='row'
        spacing={2}
      >
        {/* SearchField owns the adornment, the size and the pill. The wrapper
            owns the width, which is the one thing that differs per tab. */}
        <Box sx={{ width: 320 }}>
          <SearchField
            placeholder='Search actions'
            value={query}
            onChange={setQuery}
          />
        </Box>
        <Button
          variant='contained'
          color='primary'
          size='small'
          onClick={onCreate}
        >
          Create action
        </Button>
      </Stack>

      {/* Open work, grouped by urgency. */}
      {openGroups.map(
        (g) =>
          g.items.length > 0 && (
            <Stack key={g.label} spacing={1}>
              <GroupLabel error={g.error}>{g.label}</GroupLabel>
              {g.items.map(rowFor)}
            </Stack>
          ),
      )}

      {/* Done collects at the bottom, collapsed, with a count. A search
          opens it. */}
      {doneActions.length > 0 && (
        <Box>
          <Stack
            direction='row'
            spacing={0.5}
            onClick={() => setDoneOpen((o) => !o)}
            sx={{
              alignItems: 'center',
              cursor: 'pointer',
              width: 'fit-content',
            }}
          >
            <Icon
              icon={showDone ? 'chevron-down' : 'chevron-right'}
              fontSize={12}
            />
            <GroupLabel>Done · {doneActions.length}</GroupLabel>
          </Stack>
          <Collapse in={showDone}>
            <Stack sx={{ pt: 1 }} spacing={1}>
              {doneActions.map(rowFor)}
            </Stack>
          </Collapse>
        </Box>
      )}

      {nothing && (
        <Typography variant='body1' color='text.secondary'>
          {q
            ? 'No actions match your search.'
            : 'Nothing here yet. Create an action to give the care circle their next step.'}
        </Typography>
      )}
    </Stack>
  );
};

export default ActionsTab;
